import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    type ReactNode
} from "react";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import { ActivityUIKind } from "./constants";
import { isActiveStatus } from "./helpers";
import type { ActivitySource, ActivityViewData, ShowModalFn, SourceContext } from "./types";
import type { ActivityTrayTab } from "./ui/types";
import { ACTIVITY_REGISTRY, getTabsForKinds } from "./registry";
import { useReadState } from "./hooks/useReadState";

function useSources(sources: ActivitySource[]) {
    const [activitiesBySrc, setActivitiesBySrc] = useState<ActivityViewData[][]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (sources.length === 0) {
            setActivitiesBySrc((prev) => (prev.length === 0 ? prev : []));
            setIsLoading(false);
            return;
        }

        setActivitiesBySrc((prev) => (prev.length === 0 ? prev : []));
        setIsLoading(true);

        const emitted = new Set<number>();

        const unsubscribes = sources.map((source, i) =>
            source.subscribe((activities) => {
                emitted.add(i);
                if (emitted.size === sources.length) setIsLoading(false);
                setActivitiesBySrc((prev) => {
                    const next =
                        prev.length === sources.length
                            ? [...prev]
                            : new Array<ActivityViewData[]>(sources.length).fill([]);
                    next[i] = activities;
                    return next;
                });
            })
        );

        return () => unsubscribes.forEach((unsub) => unsub());
    }, [sources]);

    const activities = useMemo(
        () => activitiesBySrc.flat().sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        [activitiesBySrc]
    );

    const activeCount = useMemo(
        () => activities.filter((a) => isActiveStatus(a.status)).length,
        [activities]
    );

    return { activities, activeCount, isLoading };
}

interface ActivitiesContextValue {
    activities: ActivityViewData[];
    activeCount: number;
    isLoading: boolean;
    onMarkRead: (activity: ActivityViewData) => void;
    onMarkAllRead: () => void;
    tabs: ActivityTrayTab[];
}

const DEFAULT_VALUE: ActivitiesContextValue = {
    activities: [],
    activeCount: 0,
    isLoading: true,
    onMarkRead: () => {},
    onMarkAllRead: () => {},
    tabs: []
};

const ActivitiesContext = createContext<ActivitiesContextValue>(DEFAULT_VALUE);

/**
 * Context provider that owns source subscriptions and read state for the given `kinds`.
 * One subscription set per provider instance — all descendants share the same data via `useActivities()`.
 */
/** No-op fallback so consumers (e.g. Rapid) that don't render modal-launching activity kinds don't need a NotificationProvider ancestor. */
const noopShowModal: ShowModalFn = () => Promise.resolve();

export function ActivitiesProvider({
    kinds,
    workspaceId,
    userId,
    database,
    showModal = noopShowModal,
    children
}: {
    kinds: ActivityUIKind[];
    workspaceId: string;
    userId: string;
    database: DatabaseAdapter | null;
    /** Supplied by callers whose kinds include modal-launching activities (e.g. exportDataset code snippet). Omit when unused. */
    showModal?: ShowModalFn;
    children: ReactNode;
}) {
    const kindsKey = useMemo(() => [...kinds].sort().join(","), [kinds]);

    const sources: ActivitySource[] = useMemo(() => {
        const ctx: SourceContext = { workspaceId, userId, database, showModal };
        return kinds
            .map((kind) => ACTIVITY_REGISTRY[kind]?.createSource(ctx))
            .filter(Boolean) as ActivitySource[];
    }, [kindsKey, workspaceId, userId, database, showModal]);

    const { activities: sourceActivities, activeCount, isLoading } = useSources(sources);

    const { applyReadState, onMarkRead, onMarkAllRead } = useReadState({
        workspaceId,
        userId,
        database,
        sourceActivities
    });

    const activities = useMemo(
        () => applyReadState(sourceActivities),
        [sourceActivities, applyReadState]
    );

    const tabs: ActivityTrayTab[] = useMemo(() => getTabsForKinds(kinds), [kindsKey]);

    const value = useMemo(
        (): ActivitiesContextValue => ({
            activities,
            activeCount,
            isLoading,
            onMarkRead,
            onMarkAllRead,
            tabs
        }),
        [activities, activeCount, isLoading, onMarkRead, onMarkAllRead, tabs]
    );

    return <ActivitiesContext.Provider value={value}>{children}</ActivitiesContext.Provider>;
}

/** Reads activities, read state, and callbacks from the nearest `ActivitiesProvider`. Zero-arg — the provider determines which kinds are subscribed. */
export function useActivities(): ActivitiesContextValue {
    return useContext(ActivitiesContext);
}
